import { GridColDef } from '@mui/x-data-grid-premium';
import { BarSvgProps } from '@nivo/bar';
import { LineSeries, LineSvgProps } from '@nivo/line';
import { range, sortBy, uniq } from 'lodash';
import moment from 'moment';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { HeadCountReportData } from '~/data/reports';
import { legacyColors } from '~/legacyTheme';
import {
  HeadCountMonth,
  convertHeadCountReportDataToHeadCountYears,
} from '~/scenes/PortalModules/Analytics/People/HeadcountReport/data';

type PropsType = {
  data: HeadCountReportData[];
};

type HeadCountReportTableRow = {
  rowName: string;
  [p: number]: number;
};

type HeadCountBarDatum = Omit<HeadCountMonth, 'date'>;

export type UseHeadcountReportOutput = {
  charts: {
    numberOfEmployees: Pick<
      LineSvgProps<LineSeries>,
      'data' | 'yScale' | 'axisLeft' | 'colors'
    >;
    joinersAndLeavers: Pick<
      BarSvgProps<HeadCountBarDatum>,
      'data' | 'valueScale' | 'legendLabel' | 'legends' | 'tooltipLabel'
    >;
  };
  table: {
    columnConfigs: GridColDef<HeadCountReportTableRow>[];
    rows: HeadCountReportTableRow[];
  };
  yearSelector: {
    isSelectedYear: {
      first: boolean;
      last: boolean;
    };
    onNavigateYear: (direction: 'next' | 'previous') => void;
    value: string;
    onChangeYear: Dispatch<SetStateAction<string>>;
    options: string[];
  };
};

export const useHeadcountReport = (
  props: PropsType,
): UseHeadcountReportOutput => {
  const { data } = props;

  const { t } = useTranslation();

  const { headCountYears, yearOptions } = useMemo(() => {
    const headCountYears = convertHeadCountReportDataToHeadCountYears(data);
    return {
      headCountYears,
      yearOptions: headCountYears.map(({ year }) => year),
    };
  }, [data]);

  const [selectedYear, setSelectedYear] = useState<string>('');
  useEffect(() => {
    const selectedYearOutOfRange = !yearOptions.includes(selectedYear);

    if (yearOptions.length > 0 && (!selectedYear || selectedYearOutOfRange)) {
      setSelectedYear(yearOptions[yearOptions.length - 1]);
    }
  }, [selectedYear, yearOptions]);

  const { table, isSelectedYear, charts } = useMemo(() => {
    const selectedYearData = headCountYears.find(
      ({ year }) => year === selectedYear,
    );
    const monthlyCounts = selectedYearData?.monthlyCounts ?? [];

    // Charts
    const getDataCoords = (date: Date, value: number) => {
      return {
        x: moment(date).format('MMM'),
        y: value,
      };
    };

    const getNumberOfEmployeeChartMinMax = (): Pick<
      LineSvgProps<LineSeries>,
      'yScale' | 'axisLeft'
    > => {
      const sorted = uniq(
        sortBy(monthlyCounts, ['employeeCount']).map(
          (count) => count.employeeCount,
        ),
      );
      const min = (sorted[0] ?? 0) - 2;
      const max = (sorted[sorted.length - 1] ?? 0) + 2;

      const diff = Math.abs(min - max);
      const tickValues = diff < 15 ? range(min, max + 1) : undefined;

      return {
        yScale: {
          type: 'linear',
          min,
          max,
        },
        axisLeft: {
          tickValues,
        },
      };
    };

    const getChartLabel = (value: string | number) => {
      switch (value) {
        case 'employeeCount':
          return t('laNumberOfEmp');
        case 'joiners':
          return t('laJoiners');
        case 'leavers':
          return t('laLeavers');
        case 'netChange':
          return t('laNetChange');
        default:
          return '';
      }
    };

    const charts: UseHeadcountReportOutput['charts'] = {
      numberOfEmployees: {
        data: [
          {
            id: 'employeeCount',
            data: monthlyCounts.map(({ date, employeeCount }) =>
              getDataCoords(date, employeeCount),
            ),
          },
        ],
        ...getNumberOfEmployeeChartMinMax(),
        colors: (s) => {
          switch (s.id) {
            case 'employeeCount':
              return legacyColors.blue;
            default:
              return '#000000';
          }
        },
      },
      joinersAndLeavers: {
        data: monthlyCounts.map(
          ({ date, leavers, ...rest }): HeadCountBarDatum => {
            return {
              ...rest,
              leavers: leavers * -1,
              month: moment(date).format('MMM'),
            };
          },
        ),
        valueScale: {
          type: 'linear',
          min: monthlyCounts
            .map((counts) => counts.leavers * -1)
            .reduce((acc, value) => (value < acc ? value : acc), -5),
          max: monthlyCounts
            .map((counts) => counts.joiners)
            .reduce((acc, value) => (value > acc ? value : acc), 10),
        },
        legendLabel: (d) => getChartLabel(d.id),
        legends: [
          {
            dataFrom: 'keys',
            anchor: 'right',
            direction: 'column',
            itemHeight: 22,
            itemWidth: 80,
            translateX: 100,
          },
        ],
        tooltipLabel: (d) => `${d.indexValue} - ${getChartLabel(d.id)}`,
      },
    };

    // Table
    const commonColumnConfigs = {
      sortable: false,
      filterable: false,
      pinnable: false,
      hideable: false,
      disableColumnMenu: true,
    };
    const columnConfigs: GridColDef<HeadCountReportTableRow>[] = [
      {
        field: 'rowName',
        headerName: ' ',
        width: 170,
        ...commonColumnConfigs,
      },
      ...monthlyCounts.map(({ date }, index) => {
        return {
          field: `${index}`,
          headerName: moment(date).format('MMM'),
          width: 60,
          ...commonColumnConfigs,
        };
      }),
    ];

    const totalsRow = {
      rowName: t('laNumberOfEmp'),
      ...Object.fromEntries(
        monthlyCounts.map(({ employeeCount }, index) => [index, employeeCount]),
      ),
    };

    const joinersRow = {
      rowName: t('laJoiners'),
      ...Object.fromEntries(
        monthlyCounts.map(({ joiners }, index) => [index, joiners]),
      ),
    };

    const leaversRow = {
      rowName: t('laLeavers'),
      ...Object.fromEntries(
        monthlyCounts.map(({ leavers }, index) => [index, leavers]),
      ),
    };

    const netChangeRow = {
      rowName: t('laNetChange'),
      ...Object.fromEntries(
        monthlyCounts.map(({ netChange }, index) => [index, netChange]),
      ),
    };

    const isSelectedYear = {
      first: selectedYear === yearOptions[0],
      last: selectedYear === yearOptions[yearOptions.length - 1],
    };

    return {
      isSelectedYear,
      selectedYearData,
      table: {
        columnConfigs,
        rows: [totalsRow, joinersRow, leaversRow, netChangeRow],
      },
      charts,
    };
  }, [headCountYears, selectedYear, yearOptions, t]);

  const onNavigateYear = useCallback(
    (direction: 'next' | 'previous') => {
      const currentYearIndex = yearOptions.indexOf(selectedYear);
      switch (direction) {
        case 'next':
          if (!isSelectedYear.last) {
            setSelectedYear(yearOptions[currentYearIndex + 1]);
          }
          break;
        case 'previous':
          if (!isSelectedYear.first) {
            setSelectedYear(yearOptions[currentYearIndex - 1]);
          }
          break;
      }
    },
    [isSelectedYear, selectedYear, setSelectedYear, yearOptions],
  );

  return {
    table,
    charts,
    yearSelector: {
      isSelectedYear,
      onNavigateYear,
      value: selectedYear,
      onChangeYear: setSelectedYear,
      options: yearOptions,
    },
  };
};
